import React, { useState, useEffect } from "react";
import { useParams, Switch, Route, Link as RouterLink } from "react-router-dom";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import { useAuth } from "../../firebase/AuthProvider";
import {
  PublicUserProfile,
  getPublicUserProfileSnapshot,
} from "../../firebase/users";
import { useTranslation } from "react-i18next";
import Edit from "./Edit";
import Overview from "./Overview";
import Delete from "./Delete";
import ChangePassword from "./ChangePassword";
import Spinner from "../shared/Spinner";
import { makeStyles } from "@material-ui/core/styles";
import { colors } from "../../theme";

const useStyles = makeStyles({
  title: {
    fontSize: "32px",
    fontWeight: 600,
    fontStretch: "normal",
    fontStyle: "normal",
    lineHeight: "2.66",
    letterSpacing: "-0.06px",
    color: colors.TEXT_PRIMARY,
  },
  link: { 
    color: colors.DISAGREE
  }
});

const ROUTE_EDIT: string = "edit";
const ROUTE_CHANGE_PASSWORD: string = "changepassword";
const ROUTE_DELETE: string = "delete";
const routePaths = [ROUTE_EDIT, ROUTE_CHANGE_PASSWORD, ROUTE_DELETE];

type StatusType = "loading" | "notfound" | "done";

export default function ProfilePage() {
  const classes = useStyles();
  const { t } = useTranslation();
  const [status, setStatus] = useState<StatusType | null>(null);
  const { user, isAdmin, isSignedIn, initializing } = useAuth();
  const { userId: userIdInUrl } = useParams();
  const [profileId, setProfileId] = useState<string | null>();
  const [profile, setProfile] = useState<PublicUserProfile | null>(null);

  // Users are allowed to change their own profile and admins can change any profile.
  const allowedToChangeProfile =
    (isSignedIn && user && profile && user?.id === profile.id) || isAdmin;

  // Users are only allowed to change a password or delete for their own account.
  const allowedToChangePasswordAndDeleteAccount =
    user && profile && user?.id === profile.id;

  useEffect(
    function determineProfileId() {
      let id = null;

      // When no userId in the URL, the profile of the signed in user will be displayed.
      if (!userIdInUrl) id = user?.id;
      // When navigating to for example /profile/edit, the word "edit" is not a userId, but a route path.
      else if (userIdInUrl && routePaths.includes(userIdInUrl)) id = user?.id;
      // When there is a userId in the URL it will be used to find the user profile.
      else id = userIdInUrl;

      setProfileId(id);
    },
    [user, userIdInUrl, isSignedIn, initializing]
  );

  useEffect(
    function fetchUserProfile() {
      if (!profileId) {
        setProfile(null);
        return;
      }

      setStatus("loading");
      const unsubscribe = getPublicUserProfileSnapshot(
        profileId,
        (profile: PublicUserProfile | null) => {
          setStatus(profile ? "done" : "notfound");
          setProfile(profile);
        }
      );

      return () => unsubscribe();
    },
    [profileId]
  );

  function getProfileUrl(urlParam: string | undefined = undefined) {
    let url = "/profile";
    if (profileId && user && profileId !== user.id) url += "/" + profileId;
    if (urlParam) url += "/" + urlParam;
    return url;
  }

  function TemporaryMenu() {
    return (
      <Grid container item justify="center" style={{ paddingBottom: "30px" }}>
        {/* {allowedToChangeProfile && (
          <>
            <Grid style={{ margin: "10px" }}>
              <Link component={RouterLink} to={getProfileUrl()}>
                {t("profile.overview")}
              </Link>
            </Grid>
            <Grid style={{ margin: "10px" }}>
              <Link component={RouterLink} to={getProfileUrl(ROUTE_EDIT)}>
                {t("profile.editprofile")}
              </Link>
            </Grid>
          </>
        )} */}
        {/* {allowedToChangePasswordAndDeleteAccount && (
          <>
            <Grid style={{ margin: "10px" }}>
              <Link
                component={RouterLink}
                to={`/profile/${ROUTE_CHANGE_PASSWORD}`}
              >
                {t("profile.changepassword")}
              </Link>
            </Grid>
            <Grid style={{ margin: "10px" }}>
              <Link component={RouterLink} to={`/profile/${ROUTE_DELETE}`}>
                {t("profile.deleteaccount")}
              </Link>
            </Grid>
          </>
        )} */}
        <Grid style={{ margin: "10px" }}>
          <Link component={RouterLink} to={`/profile/${ROUTE_DELETE}`} className={classes.link}>
            {t("profile.deleteaccount")}
          </Link>
        </Grid>
      </Grid>
    );
  }

  if (status === "loading") return <Spinner isVisible={true} />;

  if (status === "notfound") return <div>{t("profile.notfound")}</div>;

  if (status === "done" && profile)
    return (
      <Grid container item justify="center">
        <Switch>
          <Route path={`/profile/:userId?/${ROUTE_EDIT}`}>
            <Grid container item xs={8} spacing={2}>
              <Grid item xs={12}>
                <div className={classes.title}>{t("profile.editprofile")}</div>
              </Grid>
              <Grid item xs={12}>
                <Edit userId={profile.id} />
              </Grid>
            </Grid>
            <TemporaryMenu />
          </Route>
          <Route path={`/profile/${ROUTE_DELETE}`}>
            <div className={classes.title}>{t("profile.deleteaccount")}</div>
            <Delete />
            <TemporaryMenu />
          </Route>
          <Route path={`/profile/${ROUTE_CHANGE_PASSWORD}`}>
            <div className={classes.title}>{t("profile.changepassword")}</div>
            <ChangePassword />
            <TemporaryMenu />
          </Route>
          <Route>
            <Overview profile={profile} allowedToChangeProfile={allowedToChangeProfile} />
            <TemporaryMenu />
          </Route>
        </Switch>
      </Grid>
    );

  return null;
}
